<script setup>
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import FlowMetaEventSelect from './FlowMetaEventSelect.vue';
import {
  META_ACTION_SOURCES,
  META_FIELD_SECTIONS,
  META_FIXED,
  META_IDENTITY_FIELDS,
  META_MESSAGING_CHANNELS,
  META_MESSAGING_FIELDS,
  MESSAGING_SOURCE,
  isMetaFixed,
  isMetaValueSet,
  metaFixedText,
} from './metaConversion';

const props = defineProps({
  data: { type: Object, required: true },
  fieldGroups: { type: Array, default: () => [] },
  errors: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'apply']);

const { t } = useI18n();

const draft = reactive({
  meta_event: props.data.meta_event || '',
  meta_fields: { ...props.data.meta_fields },
  meta_action_source: props.data.meta_action_source || 'system_generated',
  meta_messaging_channel: props.data.meta_messaging_channel || '',
});

const isMessaging = computed(
  () => draft.meta_action_source === MESSAGING_SOURCE
);

const channelOptions = META_MESSAGING_CHANNELS.map(value => ({
  value,
  label: t(`AUTOMATION.FLOW.META.MESSAGING_CHANNELS.${value.toUpperCase()}`),
}));

// Em mensagens comerciais só aparecem os campos do canal escolhido.
const visibleSections = computed(() =>
  META_FIELD_SECTIONS.filter(
    ({ key }) =>
      key !== 'MESSAGING' || (isMessaging.value && draft.meta_messaging_channel)
  ).map(section =>
    section.key === 'MESSAGING'
      ? {
          ...section,
          fields: section.fields.filter(({ key }) =>
            META_MESSAGING_FIELDS[draft.meta_messaging_channel].includes(key)
          ),
        }
      : section
  )
);

const fieldOptions = computed(() => [
  { value: META_FIXED, label: t('AUTOMATION.FLOW.META.FIXED_TEXT') },
  ...props.fieldGroups
    .filter(({ id }) => id !== 'conversation')
    .flatMap(group =>
      group.fields.map(field => ({
        value: field.id,
        label: `${t(group.labelKey)}: ${field.label || t(field.labelKey)}`,
      }))
    ),
]);

const INPUT_TYPES = { date_of_birth: 'date', value: 'number' };

const setField = (key, value) => {
  draft.meta_fields = { ...draft.meta_fields, [key]: value };
};

const isFixed = key => isMetaFixed(draft.meta_fields[key]);
const comboValue = key =>
  isFixed(key) ? META_FIXED : draft.meta_fields[key] || '';
const selectField = (key, value) =>
  setField(
    key,
    value === META_FIXED && isFixed(key) ? draft.meta_fields[key] : value
  );
const setFixedText = (key, text) => setField(key, `${META_FIXED}${text}`);

const actionSourceOptions = META_ACTION_SOURCES.map(value => ({
  value,
  label: t(`AUTOMATION.FLOW.META.ACTION_SOURCES.${value.toUpperCase()}`),
}));

const identityMissing = computed(
  () =>
    !isMessaging.value &&
    props.errors.includes('META_IDENTITY_REQUIRED') &&
    !META_IDENTITY_FIELDS.some(key => isMetaValueSet(draft.meta_fields[key]))
);

const messagingMissing = computed(
  () =>
    isMessaging.value &&
    props.errors.includes('META_MESSAGING_IDENTITY_REQUIRED')
);

const sectionDescriptionKey = key => {
  if (key === 'USER_DATA' && isMessaging.value) return 'DESCRIPTION_MESSAGING';
  return 'DESCRIPTION';
};

// Ao aplicar, descarta o canal e os identificadores de canais que não estão em uso.
const apply = () => {
  const allowed = isMessaging.value
    ? META_MESSAGING_FIELDS[draft.meta_messaging_channel] || []
    : [];
  const messagingKeys = Object.values(META_MESSAGING_FIELDS).flat();
  const fields = Object.fromEntries(
    Object.entries(draft.meta_fields).filter(
      ([key]) => !messagingKeys.includes(key) || allowed.includes(key)
    )
  );
  emit('apply', {
    ...draft,
    meta_fields: fields,
    meta_messaging_channel: isMessaging.value
      ? draft.meta_messaging_channel
      : '',
  });
};
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[10001] grid p-4 place-items-center bg-n-alpha-black1 backdrop-blur-[2px]"
      @pointerdown.stop
    >
      <div
        class="flex flex-col w-full max-w-lg max-h-full overflow-hidden rounded-xl bg-n-background shadow-xl outline outline-1 outline-n-container"
      >
        <div
          class="flex items-center justify-between gap-4 px-6 py-4 border-b border-n-weak"
        >
          <h2 class="mb-0 text-lg font-medium text-n-slate-12">
            {{ t('AUTOMATION.FLOW.META.MODAL_TITLE') }}
          </h2>
          <Button
            icon="i-lucide-x"
            color="slate"
            variant="ghost"
            size="sm"
            @click="emit('close')"
          />
        </div>

        <div class="flex flex-col flex-1 gap-6 px-6 py-4 overflow-y-auto">
          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('AUTOMATION.FLOW.META.EVENT') }}
            </span>
            <FlowMetaEventSelect
              v-model="draft.meta_event"
              :has-error="errors.includes('META_EVENT_REQUIRED')"
            />
          </div>

          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('AUTOMATION.FLOW.META.ACTION_SOURCE') }}
            </span>
            <ComboBox
              :model-value="draft.meta_action_source"
              :options="actionSourceOptions"
              @update:model-value="
                $event && (draft.meta_action_source = $event)
              "
            />
          </div>

          <div v-if="isMessaging" class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('AUTOMATION.FLOW.META.MESSAGING_CHANNEL') }}
            </span>
            <ComboBox
              :model-value="draft.meta_messaging_channel"
              :options="channelOptions"
              :placeholder="
                t('AUTOMATION.FLOW.META.MESSAGING_CHANNEL_PLACEHOLDER')
              "
              :has-error="errors.includes('META_CHANNEL_REQUIRED')"
              @update:model-value="
                $event && (draft.meta_messaging_channel = $event)
              "
            />
          </div>

          <section
            v-for="section in visibleSections"
            :key="section.key"
            class="flex flex-col gap-3"
          >
            <div>
              <h3 class="mb-0 text-sm font-medium text-n-slate-12">
                {{
                  t(`AUTOMATION.FLOW.META.FIELD_SECTIONS.${section.key}.TITLE`)
                }}
              </h3>
              <p
                class="mb-0 text-xs"
                :class="
                  (section.key === 'USER_DATA' && identityMissing) ||
                  (section.key === 'MESSAGING' && messagingMissing)
                    ? 'text-n-ruby-11'
                    : 'text-n-slate-11'
                "
              >
                {{
                  t(
                    `AUTOMATION.FLOW.META.FIELD_SECTIONS.${section.key}.${sectionDescriptionKey(section.key)}`
                  )
                }}
              </p>
            </div>
            <div
              v-for="field in section.fields"
              :key="field.key"
              class="flex flex-col gap-1"
            >
              <div class="flex items-baseline justify-between gap-2">
                <span class="text-sm text-n-slate-12">
                  {{
                    t(`AUTOMATION.FLOW.META.FIELDS.${field.key.toUpperCase()}`)
                  }}
                </span>
                <span
                  class="flex items-center gap-1 font-mono text-xs text-n-slate-11"
                >
                  <span
                    v-if="field.hashed"
                    class="i-lucide-lock size-3"
                    :title="t('AUTOMATION.FLOW.META.HASHED')"
                  />
                  {{ field.param }}
                </span>
              </div>
              <div class="flex items-center gap-1">
                <ComboBox
                  :model-value="comboValue(field.key)"
                  :options="fieldOptions"
                  :placeholder="t('AUTOMATION.FLOW.META.DO_NOT_SEND')"
                  :has-error="
                    identityMissing && META_IDENTITY_FIELDS.includes(field.key)
                  "
                  @update:model-value="selectField(field.key, $event)"
                />
                <Button
                  v-if="draft.meta_fields[field.key]"
                  icon="i-lucide-x"
                  color="slate"
                  variant="ghost"
                  size="sm"
                  @click="setField(field.key, '')"
                />
              </div>
              <input
                v-if="isFixed(field.key)"
                class="!mb-0 !h-8 w-full !px-2 !py-0 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 outline-none focus:border-n-brand"
                :type="INPUT_TYPES[field.key] || 'text'"
                :value="metaFixedText(draft.meta_fields[field.key])"
                :placeholder="t('AUTOMATION.FLOW.META.FIXED_PLACEHOLDER')"
                @input="setFixedText(field.key, $event.target.value)"
              />
            </div>
          </section>
        </div>

        <div class="flex gap-3 px-6 py-4 border-t border-n-weak">
          <Button
            :label="t('AUTOMATION.FLOW.META.CANCEL')"
            color="slate"
            variant="outline"
            class="flex-1"
            @click="emit('close')"
          />
          <Button
            :label="t('AUTOMATION.FLOW.META.APPLY')"
            class="flex-1"
            @click="apply"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>
